// src/pages/me/single-order.js
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Client from "../../utils/client";
import { useState, useRef } from "react";
import { toast } from "react-toastify";
import { api_url } from "../../constant";
import OrderInfo from "./components/orders/OrderInfo";

export const translateStatus = (status) => {
    switch (status) {
        case "Pending": return "قيد الانتظار";
        case "Processing": return "قيد المعالجة";
        case "Shipped": return "تم الشحن";
        case "Delivered": return "تم التوصيل";
        case "Paid": return "تم الدفع";
        case "Cancelled": return "إلغاء";
        // Service-specific
        case "WaitingForGoods" : return "في انتظار استلام البضاعة";
        case "DeliveredByBuyer": return "تم التسليم لمقدم الخدمة ";
        case "ReceivedByProvider": return "تم استلام البضاعة ";
        case "ReadyForPickup": return "جاهز للاستلام";
        case "DeliveredToClient": return "تم التسليم للعميل";
        case "ReceivedAndAccepted": return "تم الاستلام والقبول";
        default:
            return status;
    }
};

const OrderDetails = ({ currentUser }) => {
    const { orderId } = useParams();
    const queryClient = useQueryClient();
    const [selectedStatus, setSelectedStatus] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showStatusForm, setShowStatusForm] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [images, setImages] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [feedback, setFeedback] = useState("");

    const { data: order, isLoading, error } = useQuery({
        queryKey: ["order", orderId],
        queryFn: async () => {
            const response = await Client.get(`/orders/${orderId}`);
            return response.data;
        },
        enabled: !!orderId,
    });

    if (isLoading) {
        return <div className="text-center mt-10 text-gray-600">جارٍ تحميل الطلب...</div>;
    }

    if (error) {
        return <div className="text-center mt-10 text-red-500">حدث خطأ أثناء جلب بيانات الطلب</div>;
    }

    if (!order) {
        return <div className="text-center mt-10 text-red-500">الطلب غير موجود.</div>;
    }

    const isSeller = localStorage.getItem("user_id") === order?.negotiation?.sellerId;
    const isBuyer = localStorage.getItem("user_id") === order?.negotiation?.buyerId;
    const isServiceOrder = order?.negotiation?.type === "Service";

    console.log("isServiceOrder:", order.negotiation?.type, isServiceOrder);

    const goodsStatusFlow = {
        Pending: isSeller ? ["Processing", "Cancelled"] : ["Cancelled"],
        Processing: isSeller ? ["Shipped", "Cancelled"] : ["Cancelled"],
        Shipped: isBuyer ? ["Delivered", "Cancelled"] : ["Cancelled"],
        Delivered: isBuyer ? ["Paid"] : [],
        Paid: [],
    };

    const serviceStatusFlow = {
        Pending: isSeller ? ["WaitingForGoods", "Cancelled"] : ["Cancelled"],
        WaitingForGoods: isBuyer ? ["DeliveredByBuyer", "Cancelled"] : ["Cancelled"],
        DeliveredByBuyer: isSeller ? ["ReceivedByProvider", "Cancelled"] : [],
        ReceivedByProvider: isSeller ? ["ReadyForPickup", "Cancelled"] : [],
        ReadyForPickup: isSeller ? ["DeliveredToClient", "Cancelled"] : [],
        DeliveredToClient: isBuyer ? ["ReceivedAndAccepted", "Cancelled"] : [],
        ReceivedAndAccepted: isBuyer ? ["Paid", "Cancelled"] : [],
        Paid: [],
    };

    const statusFlow = isServiceOrder ? serviceStatusFlow : goodsStatusFlow;
    const allowedStatuses = order?.status ? statusFlow[order.status] || [] : [];

    const handleImageUpload = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                resolve({
                    fileObj: file,
                    preview: e.target.result
                });
            };

            reader.onerror = () => {
                reject(new Error(`Failed to process ${file.name}`));
            };

            reader.readAsDataURL(file);
        });
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        if (!e.target.files?.length) return;

        try {
            const files = Array.from(e.target.files);

            const validFiles = files.filter(file => {
                const maxSizeMB = 10;
                if (file.size > maxSizeMB * 1024 * 1024) {
                    toast.error(`حجم الملف ${file.name} تخطى ${maxSizeMB}MB`);
                    return false;
                }
                if (!file.type.startsWith('image/')) {
                    toast.error(`الملف ${file.name} ليس صورة`);
                    return false;
                }
                return true;
            });

            if (validFiles.length === 0) return;

            if (images.length + validFiles.length > 5) {
                toast.error("يمكنك رفع 5 صورة فقط كحد أقصى");
                return;
            }

            const results = await Promise.all(
                validFiles.map(file => handleImageUpload(file))
            );

            setMediaPreviews(prev => [...prev, ...results.map(r => r.preview)]);
            setImages(prev => [...prev, ...results.map(r => r.fileObj)]);

            e.target.value = '';
        } catch (error) {
            toast.error('حدثت مشكلة اثناء معالجة الملفات');
            console.error(error);
        }
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setMediaPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const uploadImages = async (files, category) => {
        try {
            const formData = new FormData();
            files.forEach((file) => {
                formData.append("images", file);
            });

            const response = await fetch(`${api_url}/files/upload-images/order/${order.id}?category=${category}`, {
                method: "POST",
                body: formData,
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "فشل في تحميل الصور");
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    };

    const updateOrderStatus = async (newStatus) => {
        setIsSubmitting(true);
        try {
            if (images.length > 0) {
                setIsUploading(true);
                try {
                    const category = newStatus === "Paid" ? "payment" : "delivery";
                    await uploadImages(images, category);
                    setImages([]);
                    setMediaPreviews([]);
                    toast.success("تم تحميل الصور بنجاح");
                } finally {
                    setIsUploading(false);
                }
            }

            if ((newStatus === "Delivered" || newStatus === "ReceivedAndAccepted") && !feedback.trim()) {
                toast.error("الرجاء كتابة الملاحظات");
                return;
            }

            const response = await Client.patch(`/orders/${order.id}/status`, {
                status: newStatus,
                reason: cancelReason,
                feedback,
            });

            queryClient.invalidateQueries(["order", orderId]);
            setShowCancelModal(false);
            setCancelReason("");
            setFeedback("");
            toast.success("تم تحديث حالة الطلب بنجاح");
            return response;

        } catch (error) {
            toast.error(error.message || "فشل في تحديث حالة الطلب");
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    const handleStatusUpdate = async () => {
        if (selectedStatus === "Cancelled") {
            setShowCancelModal(true);
        } else {
            await updateOrderStatus(selectedStatus);
        }
    };

    const handleConfirmCancel = async () => {
        if (cancelReason.trim()) {
            await updateOrderStatus("Cancelled");
        }
    };

    return (
        <div dir="rtl" className="max-w-4xl mx-auto px-6 py-10 bg-white rounded-xl shadow-md">
            <h1 className="text-2xl font-bold text-primary mb-6">تفاصيل الطلب</h1>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <OrderInfo
                    order={order}
                    setShowStatusForm={setShowStatusForm}
                    allowedStatuses={allowedStatuses}
                />

                {allowedStatuses.length > 0 && (
                    <div className="mt-4 flex gap-2 items-center">
                        <button
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            onClick={() => setShowStatusForm(true)}
                        >
                            تحديث الحالة
                        </button>
                    </div>
                )}

                {/* Status Update Section */}
                {showStatusForm &&
                    <div className="mt-8 p-6 border rounded-lg">
                        <h2 className="text-xl font-bold mb-4">تحديث حالة الطلب</h2>

                        <div className="mb-4">
                            <select
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                value={selectedStatus}
                                onChange={handleStatusChange}
                            >
                                <option value="">اختر الحالة الجديدة</option>
                                {allowedStatuses.map((status, index) => (
                                    <option key={index} value={status}>
                                        {/* {status} */}
                                        {translateStatus(status)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {(selectedStatus === "Delivered" || selectedStatus === "Paid" || selectedStatus === "ReceivedAndAccepted") && (
                            <div className="mb-4">
                                <p className="font-bold mb-2">
                                    {selectedStatus === "Paid"
                                        ? "إرفاق صور لإثبات الدفع"
                                        : "إرفاق صور للتسليم"}
                                </p>
                                <div
                                    className="w-full h-[20vh] border border-gray-300 bg-gray-50 rounded-md flex justify-center items-center text-gray-500"
                                    onClick={triggerFileInput}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        multiple
                                    />
                                    <span className="cursor-pointer text-blue-500 underline">
                                        تحميل صور
                                    </span>
                                </div>

                                {mediaPreviews?.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-sm text-gray-600 mb-2">الصور المرفوعة:</p>
                                        <div className="flex flex-wrap gap-3">
                                            {mediaPreviews.map((src, index) => (
                                                <div key={index} className="relative">
                                                    <img
                                                        src={src}
                                                        className="w-16 h-16 object-cover border border-gray-300 rounded"
                                                        alt={`Preview ${index + 1}`}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'data:image/svg+xml;base64,...';
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                                                        onClick={() => removeImage(index)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    يمكنك تحميل حتى 5 صور كحد أقصى
                                </p>


                                {/* Feedback field for Delivered status */}
                                {(selectedStatus === "Delivered" || selectedStatus === "ReceivedAndAccepted") && (
                                    <div className="mt-4">
                                        <label className="font-bold">ملاحظات العميل</label>
                                        <textarea
                                            className="w-full p-2 border rounded mt-2"
                                            placeholder="أضف ملاحظات حول الطلب..."
                                            value={feedback}
                                            onChange={(e) => setFeedback(e.target.value)}
                                        ></textarea>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                                onClick={() => {
                                    setSelectedStatus("");
                                    setImages([]);
                                    setMediaPreviews([]);
                                }}
                                disabled={isSubmitting || isUploading}
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                                disabled={!selectedStatus || isSubmitting || isUploading}
                                onClick={handleStatusUpdate}
                            >
                                {isUploading ? "جاري التحميل..." : isSubmitting ? "جاري الحفظ..." : "تأكيد التحديث"}
                            </button>
                        </div>
                    </div>
                }

                {showCancelModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-xl shadow-xl w-96">
                            <h2 className="text-xl font-bold mb-4 text-red-600">تأكيد إلغاء الطلب</h2>
                            <textarea
                                className="w-full p-2 border rounded h-24 focus:outline-none focus:ring-2 focus:ring-red-400"
                                placeholder="اكتب سبب الإلغاء..."
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                            ></textarea>
                            <div className="flex justify-end gap-4 mt-4">
                                <button
                                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                                    onClick={() => setShowCancelModal(false)}
                                    disabled={isSubmitting}
                                >
                                    إلغاء
                                </button>
                                <button
                                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                                    onClick={handleConfirmCancel}
                                    disabled={!cancelReason.trim() || isSubmitting}
                                >
                                    {isSubmitting ? "جاري الإلغاء..." : "تأكيد الإلغاء"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default OrderDetails;